/**
 * modernizeLogs.ts — Utilidad para migrar logs antiguos a la nueva nomenclatura.
 *
 * Qué hace: Mapea logs con entidad genérica "caja" a sus nuevos sub-módulos
 *           especializados (terminales, turnos, facturacion, tesoreria)
 *           basándose en la acción realizada.
 */

import { Log } from "../types/auditoria.types";

const CAJA_MAPPING: Record<string, string> = {
  // Créditos (Caja)
  cliente_registrado: "creditos",
  caja_venta_credito: "creditos",

  // Terminales (Caja)
  caja_punto_creado: "terminales",
  caja_punto_editado: "terminales",
  caja_punto_eliminado: "terminales",
  punto_pos_creado: "terminales",
  punto_pos_editado: "terminales",
  punto_pos_eliminado: "terminales",

  // Turnos (Caja)
  caja_abierta: "turnos",
  caja_cerrada: "turnos",
  caja_arqueo_fallido: "turnos",
  caja_arqueo_iniciado: "turnos",
  caja_reporte_parcial_pedido: "turnos",
  caja_reporte_parcial_visto: "turnos",
  caja_reporte_parcial_compartido: "turnos",
  caja_arqueo_realizado: "turnos",
  caja_revisar_auditoria_parcial_visualizada: "turnos",

  // Ventas (Caja) — nueva venta completada / eliminada
  caja_venta_completada: "ventas",
  venta_creada: "ventas",
  venta_eliminada: "ventas",

  // Facturación → Ventas (anulaciones y devoluciones pertenecen a Ventas Individuales)
  facturacion_venta_anulada: "ventas",
  facturacion_devolucion_procesada: "ventas",

  // Tesorería (Caja)
  caja_gasto_registrado: "tesoreria",
  caja_movimiento_pedido: "tesoreria",
  caja_ingreso_registrado: "tesoreria",
  caja_gasto_cierre_finalizado: "tesoreria",
  caja_retiro_registrado: "tesoreria",
  caja_gasto_cierre_pedido: "tesoreria",
  caja_movimiento_anulado: "tesoreria",
};

/**
 * Acciones que estaban mal clasificadas como "facturacion" pero pertenecen a "ventas".
 * Necesario para migrar logs ya guardados en localStorage antes de la corrección.
 */
const FACTURACION_TO_VENTAS_ACTIONS = new Set([
  "caja_venta_completada",
  "venta_creada",
  "venta_eliminada",
  // Anulaciones y devoluciones también pertenecen a ventas
  "facturacion_venta_anulada",
  "facturacion_devolucion_procesada",
]);

/**
 * Acciones persistidas antes del renombre fiado→crédito. Al cargar los logs
 * se normalizan a la nomenclatura actual para no perder historial de auditoría.
 */
const LEGACY_CREDITO_ACTIONS: Record<string, string> = {
  caja_venta_fiada: "caja_venta_credito",
  fiado_abono_registrado: "credito_abono_registrado",
};

/** Módulos persistidos antes del renombre fiado→crédito. */
const LEGACY_CREDITO_ENTIDADES = new Set(["fiados"]);

/**
 * Normaliza un log individual:
 * 1. Si tiene entidad "caja" (legacy), lo mapea al sub-módulo correcto por acción.
 * 2. Si tiene entidad "facturacion" pero la acción corresponde a "ventas",
 *    lo corrige (migra logs guardados antes de la corrección del service).
 * 3. Migra acciones/módulos del renombre fiado→crédito.
 */
export function modernizeLog(log: Log): Log {
  let out = log;

  if (LEGACY_CREDITO_ENTIDADES.has(out.entidad)) {
    out = { ...out, entidad: "creditos" };
  }
  const legacyAction = LEGACY_CREDITO_ACTIONS[out.action];
  if (legacyAction) {
    out = { ...out, action: legacyAction };
  }

  // Caso 1: entidad legacy genérica "caja" → remap por acción
  if (out.entidad === "caja") {
    const targetEntity = CAJA_MAPPING[out.action];
    if (targetEntity) {
      return { ...out, entidad: targetEntity };
    }
    return out;
  }

  // Caso 2: ventas mal clasificadas como "facturacion" (logs anteriores al fix)
  if (
    out.entidad === "facturacion" &&
    FACTURACION_TO_VENTAS_ACTIONS.has(out.action)
  ) {
    return { ...out, entidad: "ventas" };
  }

  return out;
}

/**
 * Normaliza una lista completa de logs.
 */
export function modernizeLogs(logs: Log[]): Log[] {
  return logs.map(modernizeLog);
}
